/**
 * The go-to pin (spec 13) — a single point + display name, persisted so it
 * survives a reload. Manual localStorage load/save (same shape as
 * unitsStore.ts/trailStore.ts) rather than zustand's `persist` middleware,
 * matching the rest of the app. Fully client-side: setting/clearing a pin
 * for a tapped point never touches the network.
 */
import { create } from 'zustand';

export interface GotoTarget {
  point: { lat: number; lon: number };
  name: string;
}

const KEY = 'los.goto';

function load(): GotoTarget | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<GotoTarget> | null;
    const lat = parsed?.point?.lat;
    const lon = parsed?.point?.lon;
    if (typeof lat === 'number' && typeof lon === 'number' && typeof parsed?.name === 'string') {
      return { point: { lat, lon }, name: parsed.name };
    }
  } catch {
    /* private mode / corrupt value */
  }
  return null;
}

interface GotoState {
  target: GotoTarget | null;
  set: (target: GotoTarget) => void;
  clear: () => void;
}

export const useGotoStore = create<GotoState>((set) => ({
  target: load(),
  set: (target) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(target));
    } catch {
      /* ignore */
    }
    set({ target });
  },
  clear: () => {
    try {
      localStorage.removeItem(KEY);
    } catch {
      /* ignore */
    }
    set({ target: null });
  },
}));
